import { NATIVE_TOKEN_LABEL } from "@constants/constants";
import useHashConnect from "@hooks/useHashConnect";
import useAccount from "@hooks/useUserAccount";
import { ClickAwayListener } from "@material-ui/core";
import { CheckCircle, Info } from "@material-ui/icons";
import { nativeTokenFormatter } from "@utils/CurrencyHelper";
import classNames from "classnames";
import { ConnectType } from "context/HashConnectProvider";
import { getAnalytics, logEvent, setUserId } from "firebase/analytics";
import { FC, useCallback, useState } from "react";
import { Button, Typography } from "../../atoms";
import Icon from "../../atoms/Icon/Icon";
import { ConnectedWalletModal, DisconnectWalletModal } from "./WalletModals";
import styles from "./WalletSelector.module.scss";

export const WalletStatus = {
  WALLET_CONNECTED: "WALLET_CONNECTED",
  INITIALIZING: "INITIALIZING",
  WALLET_NOT_CONNECTED: "WALLET_NOT_CONNECTED",
};

const WalletSelector = ({
  variant,
  size,
  iconOnly,
}: {
  variant?: any;
  size?: any;
  iconOnly?: boolean;
}) => {
  const [modal, setModal] = useState<{ open: boolean; anchorEl: any }>({
    open: false,
    anchorEl: null,
  });

  const {
    connect,
    disconnect,
    selectedAccount,
    status,
    installedExtensions,
    isBladeExtensionInstalled,
  } = useHashConnect();
  const { hbar } = useAccount();

  const isWalletConnected: boolean = status === WalletStatus.WALLET_CONNECTED;
  const isWalletInitializing: boolean = status === WalletStatus.INITIALIZING;
  const isWalletDisconnected: boolean =
    status === WalletStatus.WALLET_NOT_CONNECTED;

  const openModal = (e: any) => {
    setModal({ open: true, anchorEl: e.currentTarget });
  };
  const closeModal = () => {
    setModal({ open: false, anchorEl: null });
  };

  const connectedWalletModal = (
    <ConnectedWalletModal
      walletBalance={hbar}
      disconnectWallet={() => {
        const analytics = getAnalytics();
        logEvent(analytics, "disconnect_wallet_click", {
          account: selectedAccount,
        });
        closeModal();
        disconnect();
      }}
      walletAddress={selectedAccount}
      truncatedWalletAddress={selectedAccount}
    />
  );

  const disconnectWalletModal = (
    <DisconnectWalletModal
      installedExtensions={installedExtensions}
      isWalletInitializing={isWalletInitializing}
      isWalletDisconnected={isWalletDisconnected}
      isBladeExtensionInstalled={isBladeExtensionInstalled}
      installWallet={(type: ConnectType) => {
        const analytics = getAnalytics();
        const walletName =
          type === ConnectType.BLADE_WALLET ? "Blade" : "HashPack";
        logEvent(analytics, "selected_wallet", {
          account: selectedAccount,
          value: walletName,
        });
        closeModal();
        connect(type);
      }}
    />
  );

  const WalletButton: FC = (props) => {
    return (
      <Button
        variant={variant || "outlined"}
        icon={
          variant !== "solid" ? (
            <div className={styles.wallet_icon}>
              {/* <img
                alt=""
                src={greenTick}
                width="10"
                className={styles.wallet_connected_badge}
              /> */}

              <Icon name={"wallet"} height={20} width={20} />
            </div>
          ) : undefined
        }
        className={"px-4 items-center flex nowrap align-right"}
        onClick={openModal}
        // onClick={() => {
        //   if (!isWalletConnected) connect();
        // }}
        size={size || "small"}
        id="wallet-button"
      >
        {props.children}
      </Button>
    );
  };

  const renderWalletButton = useCallback(() => {
    // if (isWalletInitializing) {
    //   return <WalletButton> Initializing Wallet...</WalletButton>;
    // }
    if (isWalletDisconnected || isWalletInitializing) {
      return <WalletButton> Connect Wallet</WalletButton>;
    }
    const analytics = getAnalytics();
    setUserId(analytics, selectedAccount);
    logEvent(analytics, "connect_wallet_click", { account: selectedAccount });
    return (
      <WalletButton>
        <Typography
          variant={"body2"}
          fontWeight={"medium"}
          className={"inline ml-4 capitalize"}
        >
          {selectedAccount}
        </Typography>
        <div className={styles.divider} />
        <Typography variant={"body2"} fontWeight={"bold"} className={"mr-1"}>
          {nativeTokenFormatter(hbar)}
        </Typography>
        <Typography variant={"body3"} fontWeight={"bold"} className={"inline"}>
          {NATIVE_TOKEN_LABEL}
        </Typography>
      </WalletButton>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, hbar, selectedAccount]);

  const iconOnlyWalletButton = useCallback(() => {
    // if(isWalletInitializing){
    return (
      <div className={styles.icon_only_wallet} onClick={openModal}>
        <Icon name={"wallet"} height={20} width={20} />
        {isWalletConnected && (
          <CheckCircle
            className={classNames(
              styles.badge_icon,
              styles.badge_wallet_connected
            )}
          />
        )}
        {isWalletDisconnected && (
          <Info
            className={classNames(
              styles.badge_icon,
              styles.badge_wallet_disconnected
            )}
          />
        )}
      </div>
    );
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div style={{ position: "relative" }}>
      <div>{iconOnly ? iconOnlyWalletButton() : renderWalletButton()}</div>

      {modal.open && (
        <ClickAwayListener onClickAway={closeModal}>
          <div className={styles.wallet_dropdown_container}>
            <WalletDropDown>
              {isWalletConnected && connectedWalletModal}
              {!isWalletConnected && disconnectWalletModal}
            </WalletDropDown>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default WalletSelector;

const WalletDropDown: FC = (props) => {
  const { children } = props;
  return <div className={`${styles.dropdown_box} bg-dark-50`}>{children}</div>;
};
